import { useSyncExternalStore } from "react";

// Install support for the PWA (manifest + service worker via vite-plugin-pwa).
//
// Chrome / Edge / Samsung Internet (Android and desktop) fire
// `beforeinstallprompt` when the app is installable; keeping that event lets
// us show our own "App installieren" button that opens the browser prompt.
// The event can fire before React mounts, so capturing starts in main.tsx.
//
// iOS/iPadOS has no such event — installing only works via Safari's share
// menu ("Zum Home-Bildschirm"), so there we show instructions instead.

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

export function initPwaInstallCapture() {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault(); // keep it for our own button instead of the mini-infobar
    deferredPrompt = e as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    installed = true;
    notify();
  });
}

/** Running as installed app (home screen / app window), not in a browser tab. */
export function isStandalone(): boolean {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/** iPhone / iPad (incl. iPadOS, which reports itself as a Mac with touch). */
export function isIos(): boolean {
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

export function isAndroid(): boolean {
  return /Android/i.test(navigator.userAgent);
}

// prompt:  browser offered an install prompt -> our button opens it
// ios:     iPhone/iPad -> Safari share menu steps
// manual:  no prompt (yet) — dev server without service worker, Firefox,
//          in-app browsers, Chrome deciding not to offer it right now, ... ->
//          steps for this device instead of hiding the button
// null:    already installed / running as the installed app
export type InstallMode = "prompt" | "ios" | "manual" | null;

let snapshot: { mode: InstallMode } = { mode: null };
function currentMode(): InstallMode {
  if (installed || isStandalone()) return null;
  if (deferredPrompt) return "prompt";
  if (isIos()) return "ios";
  return "manual";
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  const mode = currentMode();
  if (snapshot.mode !== mode) snapshot = { mode };
  return snapshot;
}

export function usePwaInstall() {
  const { mode } = useSyncExternalStore(subscribe, getSnapshot);
  const promptInstall = async (): Promise<boolean> => {
    if (!deferredPrompt) return false;
    const event = deferredPrompt;
    deferredPrompt = null; // a prompt can only be shown once
    await event.prompt();
    const { outcome } = await event.userChoice;
    if (outcome === "accepted") installed = true;
    notify();
    return outcome === "accepted";
  };
  return { mode, promptInstall };
}
